import React, { useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { motion } from 'framer-motion';
import Header from './components/Header';
import VideoForm from './components/VideoForm';
import VideoInfo from './components/VideoInfo';
import FormatList from './components/FormatList';
import { Container, Box } from '@mui/material';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00ff9d',
    },
    secondary: {
      main: '#ff0099',
    },
    background: {
      default: '#0a0a0a',
      paper: '#1a1a1a',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

const queryClient = new QueryClient();

interface VideoData {
  title: string;
  thumbnail: string;
  duration: number;
  formats: any[];
}

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://shaaz.tubience.com'
  : 'http://localhost/ytd2';

function App() {
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVideoSubmit = async (url: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api.php?url=${encodeURIComponent(url)}`);
      const data = await response.json();
      
      if (data.success) {
        setVideoData(data.data);
      } else {
        setError(data.error || 'Failed to fetch video information');
      }
    } catch (err) {
      setError('An error occurred while fetching video information');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Container maxWidth="lg">
            <Box sx={{ minHeight: '100vh', py: 4 }}>
              <Header />
              <VideoForm onSubmit={handleVideoSubmit} loading={loading} />
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Box sx={{ color: 'error.main', mt: 2, textAlign: 'center' }}>
                    {error}
                  </Box>
                </motion.div>
              )}
              {videoData && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <VideoInfo video={videoData} />
                  <FormatList formats={videoData.formats} />
                </motion.div>
              )}
            </Box>
          </Container>
        </motion.div>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
